import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MapTrifold, Trash, FloppyDisk, Info } from "@phosphor-icons/react";

/**
 * Tour-Karte für den Mandanten: alle Kunden als Pins, einkreisbares Startgebiet.
 * Speicherung als Polygon [[lng,lat],...] auf tenants.tour_start_polygon.
 * Die Tour-Optimierung (5-Uhr-Cron) kann später auf dieses Polygon zurückgreifen.
 */
export default function TourMapPanel({ tenant }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const [data, setData] = useState({ points: [], tour_start_polygon: [], learned_routes: [] });
  const [token, setToken] = useState(null);
  const [msg, setMsg] = useState(null);
  const [inside, setInside] = useState(0);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!tenant?.tenant_id) return;
    (async () => {
      try {
        const [tm, cfg] = await Promise.all([
          api.get(`/tenants/${tenant.tenant_id}/tour-map`),
          api.get("/mapbox/config"),
        ]);
        setData(tm.data);
        setToken(cfg.data?.public_token || null);
      } catch (e) { console.warn("[TourMapPanel] load failed", e); }
    })();
  }, [tenant?.tenant_id]);

  useEffect(() => {
    if (!token || !data.points || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = token;
    const center = data.points.length > 0
      ? [data.points[0].lng, data.points[0].lat]
      : [10.45, 51.16]; // Center of Germany fallback
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center,
      zoom: 10,
      cooperativeGestures: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    // Draw
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      defaultMode: "simple_select",
    });
    map.addControl(draw, "top-left");
    drawRef.current = draw;

    map.on("load", () => {
      // Fit to points
      if (data.points.length > 1) {
        const b = new mapboxgl.LngLatBounds();
        data.points.forEach((p) => b.extend([p.lng, p.lat]));
        map.fitBounds(b, { padding: 80, maxZoom: 13, duration: 0 });
      } else if (data.points.length === 1) {
        map.jumpTo({ center: [data.points[0].lng, data.points[0].lat], zoom: 13 });
      }
      // Add customer pins as circle layer
      map.addSource("customers", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: data.points.map((p) => ({
            type: "Feature",
            geometry: { type: "Point", coordinates: [p.lng, p.lat] },
            properties: { name: p.name, address: `${p.street || ""}, ${p.zip || ""} ${p.city || ""}` },
          })),
        },
      });
      map.addLayer({
        id: "customers-layer",
        type: "circle",
        source: "customers",
        paint: {
          "circle-radius": 6,
          "circle-color": "#0e1a23",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });
      map.on("mouseenter", "customers-layer", () => (map.getCanvas().style.cursor = "pointer"));
      map.on("mouseleave", "customers-layer", () => (map.getCanvas().style.cursor = ""));
      const popup = new mapboxgl.Popup({ closeButton: false, offset: 10 });
      map.on("mouseenter", "customers-layer", (e) => {
        const f = e.features?.[0];
        if (!f) return;
        popup.setLngLat(f.geometry.coordinates)
          .setHTML(`<div style="font-family:system-ui;font-size:12px"><strong>${f.properties.name}</strong><br/>${f.properties.address}</div>`)
          .addTo(map);
      });
      map.on("mouseleave", "customers-layer", () => popup.remove());

      // Existing polygon → load
      if (data.tour_start_polygon && data.tour_start_polygon.length >= 3) {
        const coords = [...data.tour_start_polygon];
        if (coords[0][0] !== coords[coords.length-1][0] || coords[0][1] !== coords[coords.length-1][1]) {
          coords.push(coords[0]); // Close ring
        }
        draw.add({ type: "Feature", geometry: { type: "Polygon", coordinates: [coords] }, properties: {} });
      }
    });

    const onDrawChange = () => {
      setDirty(true);
      const all = draw.getAll();
      const feat = all?.features?.[0];
      const coords = feat?.geometry?.coordinates?.[0];
      if (coords) {
        // Count inside client-side for immediate feedback
        const insideCount = data.points.filter((p) => pointInPolygon(p.lng, p.lat, coords)).length;
        setInside(insideCount);
      } else {
        setInside(0);
      }
    };
    map.on("draw.create", onDrawChange);
    map.on("draw.update", onDrawChange);
    map.on("draw.delete", onDrawChange);

    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [token, data]);

  const save = async () => {
    const draw = drawRef.current;
    if (!draw) return;
    const all = draw.getAll();
    const feat = all?.features?.[0];
    const ring = feat?.geometry?.coordinates?.[0] || [];
    // strip closing duplicate
    const poly = ring.length > 3 && ring[0][0] === ring[ring.length-1][0] && ring[0][1] === ring[ring.length-1][1]
      ? ring.slice(0, -1)
      : ring;
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/tour-start-polygon`, { polygon: poly });
      setMsg({ ok: true, msg: `Gespeichert — ${r.data.customers_in_start_area} Kunden im Startgebiet` });
      setDirty(false);
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    }
  };

  const clearPoly = () => {
    const draw = drawRef.current;
    if (!draw) return;
    draw.deleteAll();
    setInside(0);
    setDirty(true);
  };

  return (
    <div className="space-y-4" data-testid="tour-map-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <MapTrifold size={22} weight="bold" /> Tour-Karte & Startgebiet
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Alle Kunden mit Adresse sind sichtbar. Umkreisen Sie das <strong>Startgebiet</strong> — bei der nächsten Tour-Optimierung werden diese Adressen zuerst abgefahren. Das System merkt sich die Fahrer-Reihenfolge und übernimmt sie automatisch beim nächsten Mal.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={clearPoly} variant="outline" className="rounded-none h-10" data-testid="polygon-clear-btn">
            <Trash size={13} weight="bold" className="mr-1" /> Löschen
          </Button>
          <Button onClick={save} disabled={!dirty} className="rounded-none bg-black text-white hover:bg-zinc-800 h-10 disabled:opacity-50" data-testid="polygon-save-btn">
            <FloppyDisk size={13} weight="bold" className="mr-1" /> Startgebiet speichern
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 p-3 rounded bg-blue-50 border border-blue-200 text-xs text-blue-900">
        <Info size={14} weight="fill" className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Bedienung:</strong> Klicken Sie oben links auf das Polygon-Icon, dann klicken Sie mehrere Punkte auf der Karte, um ein Startgebiet einzuzeichnen. Doppelklick zum Beenden. Drücken Sie „Löschen" um zu resetten.
        </div>
      </div>

      <div className="relative rounded-lg overflow-hidden border border-black/10 shadow-sm bg-white">
        <div ref={containerRef} className="w-full h-[600px]" />
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 text-xs font-medium border border-black/10 shadow-sm flex items-center gap-3">
          <span><strong>{data.points.length}</strong> Kunden auf Karte</span>
          {inside > 0 && (
            <span className="text-emerald-700 font-bold">→ <strong>{inside}</strong> im Startgebiet</span>
          )}
          {data.learned_routes?.length > 0 && (
            <span className="text-blue-700 font-bold" data-testid="learned-count">✓ {data.learned_routes.length} gelernte Route(n)</span>
          )}
        </div>
        {msg && (
          <div className={`absolute top-3 right-16 rounded-lg px-3 py-2 text-xs font-bold shadow-sm ${msg.ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
            {msg.ok ? "✓" : "✗"} {msg.msg}
          </div>
        )}
      </div>

      <details className="rounded-lg border border-black/10 bg-white p-4" data-testid="learned-routes-details">
        <summary className="text-sm font-bold cursor-pointer">Gelernte Routen ({data.learned_routes?.length || 0})</summary>
        <div className="mt-3 space-y-2 text-xs">
          {(data.learned_routes || []).length === 0 && (
            <div className="text-zinc-400 italic">Noch keine gelernten Routen — sobald der Fahrer einen Tag abschließt, wird die Reihenfolge hier vorgemerkt und beim nächsten Mal vorgeschlagen.</div>
          )}
          {(data.learned_routes || []).map((r, i) => (
            <div key={`${r.zone_id || "none"}-${r.weekday ?? "x"}-${i}`} className="p-2 border border-black/5 rounded flex flex-wrap justify-between gap-2">
              <span>Zone: {r.zone_id || "—"} · Wochentag: {r.weekday ?? "—"} · <strong>{r.ordered_customer_ids.length} Stopps</strong></span>
              <span className="text-emerald-700 font-bold">{r.runs}× bestätigt</span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

// simple ray-cast point-in-polygon (client-side preview)
function pointInPolygon(lng, lat, ring) {
  let inside = false;
  const n = ring.length;
  let j = n - 1;
  for (let i = 0; i < n; i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / ((yj - yi) || 1e-12) + xi)) {
      inside = !inside;
    }
    j = i;
  }
  return inside;
}
